import moment from "moment";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

import { DeliveryDestination, trackingLink, UPS_TRACK_LOCALE_MAP } from "~/actions/deliveries";
import { isChildrenShortCourse, Status } from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Label } from "~/components/ui/label";
import { LabelledValue } from "~/components/ui/labelled-value";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import type { TDelivery } from "~/types/delivery";
import type { TPatient } from "~/types/patient";

export { PatientStatusErrorBoundary as PatientStatus };

function PatientStatusErrorBoundary(props: React.ComponentProps<typeof PatientStatus>) {
  return (
    <RemoteLogErrorBoundary component="patient_status">
      <PatientStatus {...props} />
    </RemoteLogErrorBoundary>
  );
}

function PatientStatus({ patient }: { patient: TPatient }) {
  const intl = useIntl();
  const lastCorrection = getLastCorrection(patient);
  const upcomingDelivery = getUpcomingDelivery(patient);

  return (
    <Portlet>
      <PortletTitle as="h2" iconClassName="icon-clock">
        <FormattedMessage id="pat.block.status.header" />
      </PortletTitle>

      <div className="portlet-body">
        <LabelledValue label={<FormattedMessage id="pat.block.status.status" />}>
          {lastCorrection.index > 0 && patient.status == Status.UNFILLED_CASE
            ? `${intl.formatMessage({
                id: isChildrenShortCourse(patient.course.course_id)
                  ? "pat.block.right.additional_course"
                  : "correction",
              })} ${lastCorrection.index} - `
            : null}

          <FormattedMessage id={patient.status} />
        </LabelledValue>

        {upcomingDelivery ? (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <Label readOnly>
              <FormattedMessage id="pat.block.deliveries.aligner.delivery" />
            </Label>

            <div className="tw-flex tw-gap-2">
              <Label readOnly>{upcomingDelivery.track_number}</Label>

              <a
                href={trackingLink(
                  upcomingDelivery.track_number,
                  upcomingDelivery.type,
                  UPS_TRACK_LOCALE_MAP[intl.locale],
                )}
                rel="noopener noreferrer"
                target="_blank"
              >
                <i
                  className="icon-info font-green"
                  title={intl.formatMessage({ id: "pat.block.deliveries.ups.site.track" })}
                />
              </a>
            </div>

            <NavLink
              className="tw-font-bold"
              to={`/pages/patient/${patient.patient_id}/deliveries`}
            >
              (<FormattedMessage id="pat.block.deliveries.aligner.all" />)
            </NavLink>
          </div>
        ) : null}
      </div>
    </Portlet>
  );
}

const DELIVERY_ON_STATUSES: TPatient["status"][] = [
  Status.PRODUCTION_PRINTING,
  Status.PRODUCTION_MOLDING,
  Status.PRODUCTION_MARKING,
  Status.PRODUCTION_ALLOCATING,
  Status.PRODUCTION_FABRICATING,
  Status.PRODUCTION_QUALITY_CONTROL,
  Status.FINAL_STAGE_READY,
];

function getUpcomingDelivery(patient: TPatient): TDelivery | undefined {
  if (!DELIVERY_ON_STATUSES.includes(patient.status)) {
    return undefined;
  }

  const delivery = patient.deliveries.find(
    (delivery) =>
      delivery.destination == DeliveryDestination.CLINIC &&
      delivery.called_by_phone &&
      delivery.pickup_date,
  );

  if (!delivery || !delivery.track_number) {
    return undefined;
  }

  const pickup_date = moment(delivery.pickup_date);

  if (moment().diff(pickup_date, "months", true) <= 1) {
    return delivery;
  }

  return undefined;
}
